import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import OutsideClickHandler from 'react-outside-click-handler'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Scrollbars } from 'react-custom-scrollbars'
import { FormattedMessage } from 'react-intl'
import messages from 'utils/messages'
import { CloseIcon, SearchIcon, ChartSelect } from '../../../../../../images/common'

const SelectMetricDataMenu = ({
  options = [],
  validTableName = '',
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  onSearch = (val) => null
}) => {
  const [searchValue, setSearchValue] = useState('')
  const searchInputRef = useRef(null)

  const metricOptions = useMemo(() => {
    if (!options?.length) return []

    const search = searchValue.trim().toLowerCase()
    return options
      .filter(data => data.tableName !== 'net')
      .map(data => ({
        tableName: data.tableName,
        metricOptions: data.metrics
          .filter(metric => !search || metric.name.toLowerCase().includes(search))
          .map(metric => ({
            label: metric.name,
            value: metric.name,
            tableName: data.tableName,
            isDisabled: !!(validTableName && validTableName !== data.tableName)
          }))
      }))
      .filter(data => data.metricOptions.length > 0)
  }, [options, validTableName, searchValue])

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  const handleSearch = (value) => {
    setSearchValue(value)
    onSearch?.(value)
  }

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div
        className='chart-editor-multiselect-metric-data-menu'
        onClick={event => event.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className='text-field text-field_nolabel text-field--search'>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            className="text-field__input text-field__input--search"
            onChange={(e) => handleSearch(e.target.value)}
            value={searchValue}
          />
          <div className="text-field__icon">{!searchValue && <SearchIcon />}</div>
          {searchValue && (
            <span
              className="cursor text-field__icon text-field__icon_clickable"
              onClick={() => handleSearch('')}
            >
              <CloseIcon />
            </span>
          )}
        </div>
        <Scrollbars
          autoHeight
          className="custom-scrollbars"
          autoHeightMax={214}
          universal
          renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
          renderView={props => (
            <div
              {...props}
              style={{ ...props.style, overscrollBehavior: 'contain' }}
            />
          )}
        >
          {metricOptions.length === 0 ? (
            <div className='chart-editor-multiselect-metric-data-menu__option'>
              <FormattedMessage {...messages.noDataAvailable} />
            </div>
          ) : (
            metricOptions.map((table, index) => (
              <React.Fragment key={table.tableName}>
                <div className='chart-editor-multiselect-metric-data-menu__header'>
                  {`TableName ${table.tableName}`.toUpperCase()}
                </div>

                {table.metricOptions.map(metricOption => (
                  <div
                    key={metricOption.value}
                    className={classNames('chart-editor-multiselect-metric-data-menu__option', {
                      'chart-editor-multiselect-metric-data-menu__option__disabled': metricOption.isDisabled
                    })}
                    onClick={() => onOptionSelected(metricOption)}
                  >
                    <div className='chart-editor-multiselect-metric-data-menu__option__icon'>
                      <ChartSelect />
                    </div>
                    <div>{metricOption.label}</div>
                  </div>
                ))}

                {metricOptions.length - 1 > index && (
                  <div className='chart-editor-multiselect-metric-data-menu__separator' />
                )}
              </React.Fragment>
            ))
          )}
        </Scrollbars>
      </div>
    </OutsideClickHandler>
  )
}

export const SingleSelect = ({
  label = '',
  placeholder = 'Select an option',
  selectedOption = null,
  options = [],
  validTableName = '',
  onChange = (val) => null,
  withMetricDataMenu = false
}) => {
  const [currentSelectedOption, setCurrentSelectedOption] = useState(selectedOption[0])
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    setCurrentSelectedOption(selectedOption[0])
  }, [selectedOption])

  const handleOnMetricDataSelectedOption = (selectedMetric) => {
    if (
      currentSelectedOption &&
      currentSelectedOption.value === selectedMetric.value &&
      currentSelectedOption.tableName === selectedMetric.tableName
    ) {
      // Clicking same option removes it
      setCurrentSelectedOption(null)
      onChange(null)
    } else {
      // Replace with new option
      setCurrentSelectedOption(selectedMetric)
      onChange(selectedMetric)
    }
    setIsMenuOpen(false)
  }

  const removeSelectedOption = () => {
    setCurrentSelectedOption(null)
    onChange(null)
  }

  const handleToggleMenu = () => {
    setIsMenuOpen(prev => !prev)
  }

  return (
    <div className='chart-editor-multiselect'>
      {label && (
        <div className='chart-editor__label'>
          <label className="text-field__label1">{label}</label>
        </div>
      )}

      <div
        className='chart-editor-multiselect-input'
        onClick={handleToggleMenu}
      >
        <div className='chart-editor-multiselect-input__box'>
          {currentSelectedOption ? (
            <div
              className="chart-editor-multiselect-input__box__selected-option"
              title={typeof currentSelectedOption.label === 'string' ? currentSelectedOption.label : String(currentSelectedOption.label)}
              aria-label={typeof currentSelectedOption.label === 'string' ? currentSelectedOption.label : String(currentSelectedOption.label)}
              onClick={event => {
                event.stopPropagation()
                removeSelectedOption()
              }}
            >
              {(() => {
                const raw = typeof currentSelectedOption.label === 'string' ? currentSelectedOption.label : String(currentSelectedOption.label)
                return raw.length > 25 ? `${raw.slice(0, 25)}…` : raw
              })()}
            </div>
          ) : placeholder ? (
            <div className="chart-editor-multiselect-input__box__placeholder">
              {placeholder}
            </div>
          ) : null}
        </div>

        <div className='chart-editor-multiselect-input__icon'>
          {isMenuOpen ? <ChevronUp /> : <ChevronDown />}
        </div>

        {withMetricDataMenu && isMenuOpen && (
          <SelectMetricDataMenu
            options={options}
            validTableName={validTableName}
            selectedOption={currentSelectedOption}   // 🔹 exclude selected from menu
            onOptionSelected={handleOnMetricDataSelectedOption}
            onClose={handleToggleMenu}
          />
        )}
      </div>
    </div>
  )
}

